import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  Image,
  Mail,
  Phone,
  Globe,
  Twitter,
  Facebook,
  Instagram,
  Upload,
  MapPin,
  Tags,
  X,
  Eye,
  EyeOff,
  RefreshCw,
  Lock,
} from "lucide-react";
import { styled } from "@mui/system";
import Navbar from "../../components/Navbar";
import { toast } from "react-toastify";
import axios from "axios";

// Password strength criteria
const passwordStrengthRegex = {
  hasNumber: /\d/,
  hasUpperCase: /[A-Z]/,
  hasLowerCase: /[a-z]/,
  hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/,
  minLength: 8,
};

// Validation schema
const validationSchema = Yup.object({
  brand_name: Yup.string().required("Brand name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone_number: Yup.string().required("Phone number is required"),
  brand_logo: Yup.mixed().required("Brand logo is required"),
  website: Yup.string().url("Invalid URL").required("Website is required"),
  twitter: Yup.string().url("Invalid URL"),
  facebook: Yup.string().url("Invalid URL"),
  instagram: Yup.string().url("Invalid URL"),
  location: Yup.string().required("Location is required"),
  brand_category: Yup.string().required("Category is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(passwordStrengthRegex.hasNumber, "Password must contain at least one number")
    .matches(passwordStrengthRegex.hasUpperCase, "Password must contain at least one uppercase letter")
    .matches(passwordStrengthRegex.hasLowerCase, "Password must contain at least one lowercase letter")
    .matches(passwordStrengthRegex.hasSpecialChar, "Password must contain at least one special character"),
});

// Styled components
const PageContainer = styled(Box)({
  minHeight: "100vh",
  padding: "20px",
  "@media (min-width: 600px)": {
    padding: "40px",
  },
  background: "linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)",
});

const FormContainer = styled(Box)({
  background: "#ffffff",
  borderRadius: "24px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
  padding: "24px",
  "@media (min-width: 600px)": {
    padding: "48px",
  },
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "8px",
    background: "linear-gradient(90deg, #FF6C6C 0%, #FF8F8F 100%)",
    borderRadius: "24px 24px 0 0",
  },
});

const TwoColumnGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "24px",
  marginBottom: "32px",
  "@media (max-width: 768px)": {
    gridTemplateColumns: "1fr",
  },
});

const StyledField = styled(TextField)({
  "& .MuiInputBase-root": {
    background: "#fff",
    borderRadius: "8px",
    fontSize: "16px",
  },
  "& .MuiInputBase-input": {
    padding: "16px 14px",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#FF6C6C",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#FF6C6C",
    },
  },
  "& .MuiInputLabel-root": {
    "&.Mui-focused": {
      color: "#FF6C6C",
    },
  },
  "& .MuiInputAdornment-root": {
    marginRight: "12px",
    "& svg": {
      color: "#FF6C6C",
    },
  },
});

const LogoUploadArea = styled(Box)({
  border: "2px dashed #FF6C6C",
  borderRadius: "16px",
  padding: "32px",
  textAlign: "center",
  backgroundColor: "#FFF8F8",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#FFF0F0",
    borderColor: "#FF5252",
  },
});

const FormSection = styled(Box)({
  background: "#f8f9fa",
  borderRadius: "16px",
  padding: "20px",
  "@media (min-width: 600px)": {
    padding: "32px",
  },
  marginBottom: "24px",
  "@media (min-width: 600px)": {
    marginBottom: "32px",
  },
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  "&:last-child": {
    marginBottom: 0,
  },
});

const SectionTitle = styled(Typography)({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  "&::before": {
    content: '""',
    width: "4px",
    height: "24px",
    background: "#FF6C6C",
    borderRadius: "2px",
  },
});

const LogoPreviewContainer = styled(motion.div)({
  marginTop: "24px",
  position: "relative",
  width: "200px",
  height: "200px",
  margin: "24px auto 0",
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
});

const LogoPreviewImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  padding: "16px",
  backgroundColor: "#fff",
});

const RemoveLogoButton = styled(motion.button)({
  position: "absolute",
  top: "8px",
  right: "8px",
  background: "rgba(255, 108, 108, 0.9)",
  border: "none",
  borderRadius: "50%",
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "#fff",
  "&:hover": {
    background: "#FF5252",
  },
});

const StyledSelect = styled(Select)({
  "& .MuiSelect-select": {
    padding: "16px 14px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#FF6C6C",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#FF6C6C",
  },
  backgroundColor: "#fff",
});

const BRAND_CATEGORIES = [
  "Electronics",
  "Fashion",
  "Food & Beverage",
  "Health & Beauty",
  "Home & Garden",
  "Sports & Recreation",
  "Automotive",
  "Services",
  "Technology",
  "Finance",
  "Education",
  "Entertainment",
  "Other",
];

const StyledFormControl = styled(FormControl)({
  "& .MuiInputLabel-root": {
    "&.Mui-focused": {
      color: "#FF6C6C",
    },
  },
});

const XLogo = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 1200 1227"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 87.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1139.04H892.476L569.165 687.854V687.828Z"
      fill="currentColor"
    />
  </svg>
);

const PasswordStrengthBar = styled(LinearProgress)({
  marginTop: "8px",
  height: "4px",
  borderRadius: "2px",
  backgroundColor: "#e0e0e0",
  "& .MuiLinearProgress-bar": {
    borderRadius: "2px",
  },
});

const PasswordCriteriaList = styled(Box)({
  marginTop: "8px",
  "& > div": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: "#666",
    marginBottom: "4px",
    "& svg": {
      width: "14px",
      height: "14px",
    },
  },
});

const CreateBrand = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password) => {
    if (!password) return 0;
    let strength = 0;
    if (passwordStrengthRegex.hasNumber.test(password)) strength += 20;
    if (passwordStrengthRegex.hasUpperCase.test(password)) strength += 20;
    if (passwordStrengthRegex.hasLowerCase.test(password)) strength += 20;
    if (passwordStrengthRegex.hasSpecialChar.test(password)) strength += 20;
    if (password.length >= passwordStrengthRegex.minLength) strength += 20;
    return strength;
  };

  const generateStrongPassword = () => {
    const length = 12;
    const charset = {
      uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      lowercase: "abcdefghijklmnopqrstuvwxyz",
      numbers: "0123456789",
      special: "!@#$%^&*()_+-=[]{}|;:,.<>?"
    };
    
    let password = "";
    // Ensure at least one character from each set
    password += charset.uppercase.charAt(Math.floor(Math.random() * charset.uppercase.length));
    password += charset.lowercase.charAt(Math.floor(Math.random() * charset.lowercase.length));
    password += charset.numbers.charAt(Math.floor(Math.random() * charset.numbers.length));
    password += charset.special.charAt(Math.floor(Math.random() * charset.special.length));
    
    // Fill the rest randomly
    const allChars = Object.values(charset).join("");
    for (let i = password.length; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    // Shuffle the password
    return password.split("").sort(() => Math.random() - 0.5).join("");
  };

  const handleDrop = (event, setFieldValue) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleFileUpload(file, setFieldValue);
    }
  };

  const handleFileUpload = (file, setFieldValue) => {
    if (file && file.size <= 5 * 1024 * 1024) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        setFieldValue("brand_logo", base64String);
        setFieldValue("brand_logo_preview", base64String);
      };
      reader.onerror = () => {
        toast.error("Error reading image file");
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("File size should be less than 5MB");
    }
  };

  const submitBrandData = async (values) => {
    setIsSubmitting(true);
    
    try {
      const vendorData = {
        brand_name: values.brand_name,
        phone_number: values.phone_number,
        email: values.email,
        brand_category: values.brand_category,
        location: values.location,
        website: values.website || '',
        facebook: values.facebook || '',
        instagram: values.instagram || '',
        twitter: values.twitter || '',
        brand_logo: values.brand_logo || null,
        password: values.password,
        password_confirmation: values.password,
      };

      const response = await axios.post(
        'https://backend.dealboxx.promo/api/admin/create/vendor',
        vendorData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      console.log("Brand creation response:", response.data.data.status_code);

      if (response.data?.data?.status_code === "000") {
        toast.success(response.data.data.reason || "Brand created successfully!");
        // Reset form or redirect here if needed
      } else {
        toast.error(response.data.data.reason || "Failed to create brand");
        return;
      }
      
    } catch (error) {
      console.error("Error creating brand:", error);
      toast.error(error.response?.data?.message || error.message || "An error occurred while creating brand");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar page="Create Brand" />
      <PageContainer>
        <Formik
          initialValues={{
            brand_name: "",
            email: "",
            phone_number: "",
            brand_logo: null,
            website: "",
            twitter: "",
            facebook: "",
            instagram: "",
            brand_logo_preview: null,
            location: "",
            brand_category: "",
            password: "",
          }}
          validationSchema={validationSchema}
          onSubmit={submitBrandData}
        >
          {({ setFieldValue, errors, touched, values, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <FormContainer
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Typography
                  variant="h3"
                  align="center"
                  gutterBottom
                  sx={{
                    color: "#333",
                    fontWeight: 700,
                    marginBottom: { xs: "32px", sm: "48px" },
                    fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                    "& span": {
                      color: "#FF6C6C",
                    },
                  }}
                >
                  Create New <span>Brand</span>
                </Typography>

                <FormSection
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <SectionTitle variant="h6" sx={{ marginBottom: "32px" }}>
                    Brand Identity
                  </SectionTitle>

                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: "24px", sm: "32px" },
                      flexDirection: { xs: "column", md: "row" },
                    }}
                  >
                    <Box
                      sx={{
                        flex: { xs: "1", md: "0 0 300px" },
                        maxWidth: { xs: "100%", sm: "400px", md: "300px" },
                        margin: { xs: "0 auto", md: "0" },
                      }}
                    >
                      <LogoUploadArea
                        component={motion.div}
                        whileHover={{ scale: 1.01 }}
                        onDrop={(e) => handleDrop(e, setFieldValue)}
                        onDragOver={(e) => e.preventDefault()}
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = "image/*";
                          input.onchange = (e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleFileUpload(e.target.files[0], setFieldValue);
                            }
                          };
                          input.click();
                        }}
                        sx={{
                          borderColor: errors.brand_logo && touched.brand_logo ? "#ff0000" : "#FF6C6C",
                        }}
                      >
                        <AnimatePresence mode="wait">
                          {!values.brand_logo_preview ? (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              key="upload-prompt"
                            >
                              <Upload
                                size={48}
                                color={errors.brand_logo && touched.brand_logo ? "#ff0000" : "#FF6C6C"}
                                style={{ marginBottom: "16px" }}
                              />
                              <Typography
                                variant="h6"
                                color={errors.brand_logo && touched.brand_logo ? "#ff0000" : "#FF6C6C"}
                                gutterBottom
                              >
                                Drop your brand logo here
                              </Typography>
                              <Typography variant="body2" color="#666">
                                or click to browse (PNG, JPG up to 5MB)
                              </Typography>
                              {errors.brand_logo && touched.brand_logo && (
                                <Typography variant="body2" color="#ff0000" sx={{ mt: 1 }}>
                                  {errors.brand_logo}
                                </Typography>
                              )}
                            </motion.div>
                          ) : (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              key="logo-preview"
                            >
                              <LogoPreviewContainer>
                                <LogoPreviewImage
                                  src={values.brand_logo_preview}
                                  alt="Brand Logo Preview"
                                />
                                <RemoveLogoButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFieldValue("brand_logo", null);
                                    setFieldValue("brand_logo_preview", null);
                                  }}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  ×
                                </RemoveLogoButton>
                              </LogoPreviewContainer>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </LogoUploadArea>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <TwoColumnGrid>
                        <Field name="brand_name">
                          {({ field, meta }) => (
                            <StyledField
                              {...field}
                              label="Brand Name"
                              variant="outlined"
                              fullWidth
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              InputProps={{
                                startAdornment: <Image size={20} />,
                              }}
                            />
                          )}
                        </Field>

                        <Field name="email">
                          {({ field, meta }) => (
                            <StyledField
                              {...field}
                              label="Email"
                              variant="outlined"
                              fullWidth
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              InputProps={{
                                startAdornment: <Mail size={20} />,
                              }}
                            />
                          )}
                        </Field>

                        <Field name="phone_number">
                          {({ field, meta }) => (
                            <StyledField
                              {...field}
                              label="Phone Number"
                              variant="outlined"
                              fullWidth
                              placeholder="233xxxxxxxxx"
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              InputProps={{
                                startAdornment: <Phone size={20} />,
                              }}
                            />
                          )}
                        </Field>

                        <Field name="website">
                          {({ field, meta }) => (
                            <StyledField
                              {...field}
                              label="Website"
                              variant="outlined"
                              fullWidth
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              InputProps={{
                                startAdornment: <Globe size={20} />,
                              }}
                            />
                          )}
                        </Field>

                        <Field name="location">
                          {({ field, meta }) => (
                            <StyledField
                              {...field}
                              label="Location"
                              variant="outlined"
                              fullWidth
                              placeholder="City, Region"
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              InputProps={{
                                startAdornment: <MapPin size={20} />,
                              }}
                            />
                          )}
                        </Field>

                        <Field name="brand_category">
                          {({ field, meta }) => (
                            <StyledFormControl
                              fullWidth
                              error={meta.touched && Boolean(meta.error)}
                            >
                              <InputLabel>Brand Category</InputLabel>
                              <StyledSelect
                                {...field}
                                label="Brand Category"
                                startAdornment={
                                  <Tags size={20} style={{ color: "#FF6C6C" }} />
                                }
                              >
                                {BRAND_CATEGORIES.map((category) => (
                                  <MenuItem key={category} value={category}>
                                    {category}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                              {meta.touched && meta.error && (
                                <FormHelperText>{meta.error}</FormHelperText>
                              )}
                            </StyledFormControl>
                          )}
                        </Field>
                      </TwoColumnGrid>
                    </Box>
                  </Box>
                </FormSection>

                <FormSection
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <SectionTitle variant="h6" sx={{ marginBottom: "32px" }}>
                    Social Media Presence
                  </SectionTitle>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                      },
                      gap: { xs: "16px", sm: "24px" },
                    }}
                  >
                    <Field name="twitter">
                      {({ field, meta }) => (
                        <StyledField
                          {...field}
                          label="X (formerly Twitter)"
                          variant="outlined"
                          fullWidth
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                          InputProps={{
                            startAdornment: (
                              <Box
                                sx={{
                                  color: "#000000",
                                  display: "flex",
                                  alignItems: "center",
                                  marginRight: "8px",
                                }}
                              >
                                <XLogo />
                              </Box>
                            ),
                          }}
                        />
                      )}
                    </Field>

                    <Field name="facebook">
                      {({ field, meta }) => (
                        <StyledField
                          {...field}
                          label="Facebook"
                          variant="outlined"
                          fullWidth
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                          InputProps={{
                            startAdornment: (
                              <Box sx={{ color: "#4267B2" }}>
                                <Facebook size={20} />
                              </Box>
                            ),
                          }}
                        />
                      )}
                    </Field>

                    <Field name="instagram">
                      {({ field, meta }) => (
                        <StyledField
                          {...field}
                          label="Instagram"
                          variant="outlined"
                          fullWidth
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                          InputProps={{
                            startAdornment: (
                              <Box sx={{ color: "#E1306C" }}>
                                <Instagram size={20} />
                              </Box>
                            ),
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </FormSection>

                <FormSection
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                >
                  <SectionTitle variant="h6" sx={{ marginBottom: "32px" }}>
                    Security
                  </SectionTitle>

                  <Box sx={{ maxWidth: "500px" }}>
                    <Field name="password">
                      {({ field, meta, form: { setFieldValue } }) => (
                        <>
                          <Box sx={{ 
                            display: "flex", 
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: "12px", sm: "16px" },
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            marginBottom: { xs: "20px", sm: "16px" }
                          }}>
                            <Button
                              variant="outlined"
                              startIcon={<RefreshCw size={18} />}
                              onClick={() => {
                                const newPassword = generateStrongPassword();
                                setFieldValue("password", newPassword);
                                setPasswordStrength(calculatePasswordStrength(newPassword));
                              }}
                              sx={{
                                borderColor: "#FF6C6C",
                                color: "#FF6C6C",
                                padding: { xs: "12px 16px", sm: "8px 16px" },
                                borderRadius: "8px",
                                textTransform: "none",
                                fontWeight: 500,
                                width: { xs: "100%", sm: "auto" },
                                minWidth: { sm: "200px" },
                                "&:hover": {
                                  borderColor: "#ff5252",
                                  background: "rgba(255, 108, 108, 0.04)",
                                },
                              }}
                            >
                              Generate Strong Password
                            </Button>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#666",
                                fontSize: { xs: "13px", sm: "12px" },
                                maxWidth: { xs: "100%", sm: "200px" },
                                textAlign: { xs: "center", sm: "left" },
                                marginTop: { xs: "0", sm: "8px" },
                              }}
                            >
                              Click to generate a secure password that meets all requirements
                            </Typography>
                          </Box>
                          <StyledField
                            {...field}
                            type={showPassword ? "text" : "password"}
                            label="Password"
                            variant="outlined"
                            fullWidth
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                            onChange={(e) => {
                              field.onChange(e);
                              setPasswordStrength(calculatePasswordStrength(e.target.value));
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock size={20} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    size="large"
                                    sx={{ 
                                      color: "#666",
                                      padding: { xs: "8px", sm: "12px" }
                                    }}
                                  >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-root": {
                                height: { xs: "48px", sm: "auto" }
                              }
                            }}
                          />
                          <PasswordStrengthBar
                            variant="determinate"
                            value={passwordStrength}
                            sx={{
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: 
                                  passwordStrength <= 20 ? "#ff4444" :
                                  passwordStrength <= 40 ? "#ffa700" :
                                  passwordStrength <= 60 ? "#ffdb4a" :
                                  passwordStrength <= 80 ? "#9acd32" :
                                  "#00c851",
                              },
                              height: { xs: "6px", sm: "4px" },
                              marginTop: { xs: "12px", sm: "8px" }
                            }}
                          />
                          <Box sx={{ 
                            display: "flex", 
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: { xs: "4px", sm: "8px" },
                            marginTop: { xs: "12px", sm: "8px" }
                          }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#666",
                                fontSize: { xs: "13px", sm: "12px" },
                              }}
                            >
                              Password Strength:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  passwordStrength <= 20 ? "#ff4444" :
                                  passwordStrength <= 40 ? "#ffa700" :
                                  passwordStrength <= 60 ? "#ffdb4a" :
                                  passwordStrength <= 80 ? "#9acd32" :
                                  "#00c851",
                                fontWeight: 600,
                                fontSize: { xs: "13px", sm: "12px" },
                              }}
                            >
                              {
                                passwordStrength <= 20 ? "Very Weak" :
                                passwordStrength <= 40 ? "Weak" :
                                passwordStrength <= 60 ? "Fair" :
                                passwordStrength <= 80 ? "Good" :
                                "Strong"
                              }
                            </Typography>
                          </Box>
                          <PasswordCriteriaList
                            sx={{
                              marginTop: { xs: "16px", sm: "8px" },
                              "& > div": {
                                marginBottom: { xs: "8px", sm: "4px" },
                                "& span": {
                                  fontSize: { xs: "13px", sm: "12px" }
                                }
                              }
                            }}
                          >
                            <Box>
                              <Typography
                                component="span"
                                sx={{
                                  color: values.password?.length >= 8 ? "#00c851" : "#666",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                • At least 8 characters
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                component="span"
                                sx={{
                                  color: passwordStrengthRegex.hasUpperCase.test(values.password) ? "#00c851" : "#666",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                • One uppercase letter
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                component="span"
                                sx={{
                                  color: passwordStrengthRegex.hasLowerCase.test(values.password) ? "#00c851" : "#666",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                • One lowercase letter
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                component="span"
                                sx={{
                                  color: passwordStrengthRegex.hasNumber.test(values.password) ? "#00c851" : "#666",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                • One number
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                component="span"
                                sx={{
                                  color: passwordStrengthRegex.hasSpecialChar.test(values.password) ? "#00c851" : "#666",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                • One special character
                              </Typography>
                            </Box>
                          </PasswordCriteriaList>
                        </>
                      )}
                    </Field>
                  </Box>
                </FormSection>

                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                  sx={{
                    marginTop: { xs: "32px", sm: "48px" },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "center",
                    gap: { xs: "12px", sm: "16px" },
                  }}
                >
                  <Button
                    variant="outlined"
                    size="large"
                    disabled={isSubmitting}
                    sx={{
                      borderColor: "#FF6C6C",
                      color: "#FF6C6C",
                      padding: { xs: "10px 24px", sm: "12px 32px" },
                      fontSize: { xs: "14px", sm: "16px" },
                      borderRadius: "12px",
                      textTransform: "none",
                      borderWidth: "2px",
                      "&:hover": {
                        borderWidth: "2px",
                        borderColor: "#ff5252",
                        background: "rgba(255, 108, 108, 0.04)",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{
                      background:
                        "linear-gradient(90deg, #FF6C6C 0%, #FF8F8F 100%)",
                      color: "#fff",
                      padding: { xs: "10px 32px", sm: "12px 48px" },
                      fontSize: { xs: "14px", sm: "16px" },
                      borderRadius: "12px",
                      textTransform: "none",
                      boxShadow: "0 4px 16px rgba(255, 108, 108, 0.3)",
                      "&:hover": {
                        background:
                          "linear-gradient(90deg, #FF5252 0%, #FF7575 100%)",
                        boxShadow: "0 6px 20px rgba(255, 108, 108, 0.4)",
                      },
                      "&:disabled": {
                        background: "#ccc",
                        color: "#666",
                      },
                      position: "relative",
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ marginRight: "8px", color: "#fff" }} />
                        Creating...
                      </>
                    ) : (
                      "Create Brand"
                    )}
                  </Button>
                </Box>
              </FormContainer>
            </Form>
          )}
        </Formik>
      </PageContainer>
    </>
  );
};

export default CreateBrand;